import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import * as i4 from '@usana/ux/common/url';
import { UrlModule } from '@usana/ux/common/url';
import { ReplaySubject } from 'rxjs';
import * as i2 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import * as i3 from '@usana/ux/ssr';
import { UsanaSsrModule } from '@usana/ux/ssr';
import * as i1 from '@angular/common/http';
import { UsanaLoggingService } from '@usana/ux/common/logging';
import { CommonModule } from '@angular/common';

const GRAPH_URL_MARKET = new InjectionToken('graphql.markets');
const I18N_INFO_CONFIG = new InjectionToken('market.i18n.config');

var MarketActiveStatus;
(function (MarketActiveStatus) {
    MarketActiveStatus[MarketActiveStatus["ACTIVE"] = 0] = "ACTIVE";
    MarketActiveStatus[MarketActiveStatus["INACTIVE"] = 1] = "INACTIVE";
    MarketActiveStatus[MarketActiveStatus["ANY"] = 2] = "ANY";
    MarketActiveStatus[MarketActiveStatus["KEEP"] = 3] = "KEEP";
})(MarketActiveStatus || (MarketActiveStatus = {}));

var I18nOrganization;
(function (I18nOrganization) {
    I18nOrganization[I18nOrganization["USANA"] = 0] = "USANA";
    I18nOrganization[I18nOrganization["BAOYING"] = 1] = "BAOYING";
    I18nOrganization[I18nOrganization["BOTH"] = 2] = "BOTH";
    I18nOrganization[I18nOrganization["KEEP"] = 3] = "KEEP";
})(I18nOrganization || (I18nOrganization = {}));

class I18nInfoConfig {
    constructor(getLanguages, getCountries, getCurrent, getTranslationMarketId, getMarkets, getPcMarkets, getMarketRollup, getPcMarketRollup, active, org, honorCookie) {
        this.getLanguages = getLanguages;
        this.getCountries = getCountries;
        this.getCurrent = getCurrent;
        this.getTranslationMarketId = getTranslationMarketId;
        this.getMarkets = getMarkets;
        this.getPcMarkets = getPcMarkets;
        this.getMarketRollup = getMarketRollup;
        this.getPcMarketRollup = getPcMarketRollup;
        this.activeStatus = active;
        this.org = org;
        this.honorCookie = honorCookie;
    }
    static largerOrg(org1, org2) {
        if (org1 === I18nOrganization.KEEP) {
            return org2;
        }
        if (org2 === I18nOrganization.KEEP) {
            return org1;
        }
        // at this point if they differ we need to return both
        if (org1 !== org2) {
            return I18nOrganization.BOTH;
        }
        return org1; // they are the same so return either
    }
    static largerActiveStatus(ms1, ms2) {
        if (ms1 === MarketActiveStatus.KEEP) {
            return ms2;
        }
        if (ms2 === MarketActiveStatus.KEEP) {
            return ms1;
        }
        // at this point if they differ we need to return any
        if (ms1 !== ms2) {
            return MarketActiveStatus.ANY;
        }
        return ms1; // they are the same so return either
    }
    static moreImportantCookie(hc1, hc2) {
        // these are ordered by verboseness so we can sort and return the lower option
        return [hc1, hc2].sort()[0];
    }
    /**
     * returns a config that is just the newly added variables (are true when other is false)
     * @param other - new config to diff against
     */
    diff(other) {
        if (!other) {
            return this;
        }
        // return a config that contains true values for newly requested items
        return new I18nInfoConfig(!other.getLanguages && this.getLanguages, !other.getCountries && this.getCountries, !other.getCurrent && this.getCurrent, !other.getTranslationMarketId && this.getTranslationMarketId, !other.getMarkets && this.getMarkets, !other.getPcMarkets && this.getPcMarkets, !other.getMarketRollup && this.getMarketRollup, !other.getPcMarketRollup && this.getPcMarketRollup, 
        // if we have different levels of active requesting we have to ask for both
        I18nInfoConfig.largerActiveStatus(this.activeStatus, other.activeStatus), I18nInfoConfig.largerOrg(this.org, other.org), I18nInfoConfig.moreImportantCookie(this.honorCookie, other.honorCookie));
    }
    /**
     * returns a config that is the combined variables of both configs (either is true)
     * @param other - new config to combine with
     */
    combined(other) {
        if (!other) {
            return this;
        }
        return new I18nInfoConfig(other.getLanguages || this.getLanguages, other.getCountries || this.getCountries, other.getCurrent || this.getCurrent, other.getTranslationMarketId || this.getTranslationMarketId, other.getMarkets || this.getMarkets, other.getPcMarkets || this.getPcMarkets, other.getMarketRollup || this.getMarketRollup, other.getPcMarketRollup || this.getPcMarketRollup, 
        // if we have different levels of active requesting we have to ask for both
        I18nInfoConfig.largerActiveStatus(this.activeStatus, other.activeStatus), I18nInfoConfig.largerOrg(this.org, other.org), I18nInfoConfig.moreImportantCookie(this.honorCookie, other.honorCookie));
    }
    requestAnything(previousActiveStatus) {
        return (previousActiveStatus !== this.activeStatus ||
            this.getLanguages ||
            this.getCountries ||
            this.getCurrent ||
            this.getTranslationMarketId ||
            this.getMarkets ||
            this.getPcMarkets ||
            this.getMarketRollup ||
            this.getPcMarketRollup);
    }
    activeStatusToBoolean() {
        switch (this.activeStatus) {
            case MarketActiveStatus.ACTIVE:
                return true;
            case MarketActiveStatus.INACTIVE:
                return false;
            case MarketActiveStatus.ANY:
                return null;
            default:
                // shouldn't be here
                return true;
        }
    }
}

const LOCALE_COOKIE = 'i18n.cookie.locale';

const marketFragment = 'currentMarketId,' + 'portalMarketId,' + 'phxMarketId,' + 'isoMarketId,' + 'country {' + 'alpha2' + '},' + 'language {' + 'alpha3' + '}';
const currentMarketFragment = 'current { ' + marketFragment + ' },';
const marketsFragment = 'markets { ' + marketFragment + '}, ';
const pcMarketsFragment = 'pcMarkets { ' + marketFragment + '}, ';
const getMarket = {
    query: 'query getMarket($marketId: String!) { \n' + '    getMarket(marketId: $marketId) {\n' + '        currentMarketId\n' + '    } \n' + '}',
    operationName: 'getMarket',
};
const graphQueries = {
    buildFromConfig: (config) => {
        const vars = [];
        if (config.getLanguages) {
            vars.push('languages');
        }
        if (config.getCountries) {
            vars.push('countries');
        }
        if (config.getCurrent) {
            vars.push(currentMarketFragment);
        }
        if (config.getTranslationMarketId) {
            vars.push('translationMarketId');
        }
        if (config.getMarkets) {
            vars.push(marketsFragment);
        }
        if (config.getPcMarkets) {
            vars.push(pcMarketsFragment);
        }
        if (config.getMarketRollup) {
            vars.push('marketRollup');
        }
        if (config.getPcMarketRollup) {
            vars.push('pcMarketRollup');
        }
        const query = 'query getI18NInfo($marketQuery: MarketQuery!) {' + 'getI18NInfo(marketQuery: $marketQuery) {' + vars.join(',') + '}' + '}';
        return {
            query: `${query}`,
            operationName: 'getI18NInfo',
        };
    },
};

// NOTE: this isn't in the .tokens.ts file because we don't want external apps to be able to specify it
const PRELOADED_INFO = new InjectionToken('market.i18n.preloaded');
const USANA_MARKET_DEFS = {
    markets: [
        {
            currentMarketId: 'enu-AU',
            portalMarketId: 'en_AU',
            phxMarketId: 'en-AU',
            odsyMarketId: 'ENU.AU',
            isoMarketId: 'en-AU',
        },
        {
            currentMarketId: 'zhs-AU',
            portalMarketId: 'zh_AU',
            phxMarketId: 'zh-AU',
            odsyMarketId: 'ZHS.AU',
            isoMarketId: 'zh-Hans-AU',
        },
        {
            currentMarketId: 'enu-CA',
            portalMarketId: 'en_CA',
            phxMarketId: 'en-CA',
            odsyMarketId: 'ENU.CA',
            isoMarketId: 'en-CA',
        },
        {
            currentMarketId: 'fre-CA',
            portalMarketId: 'fr_CA',
            phxMarketId: 'fr-CA',
            odsyMarketId: 'FRC.CA',
            isoMarketId: 'fr-CA',
        },
        {
            currentMarketId: 'zht-CA',
            portalMarketId: 'zh_CA',
            phxMarketId: 'zh-CA',
            odsyMarketId: 'ZHT.CA',
            isoMarketId: 'zh-Hant-CA',
        },
        {
            currentMarketId: 'zhs-CA',
            portalMarketId: 'zh_CA',
            phxMarketId: 'zh-CA',
            odsyMarketId: 'ZHS.CA',
            isoMarketId: 'zh-Hans-CA',
        },
        {
            currentMarketId: 'spa-CO',
            portalMarketId: 'es_CO',
            phxMarketId: 'es-CO',
            odsyMarketId: 'ESM.CO',
            isoMarketId: 'es-CO',
        },
        {
            currentMarketId: 'deu-DE',
            portalMarketId: 'de_DE',
            phxMarketId: 'de-DE',
            odsyMarketId: 'DEU.DE',
            isoMarketId: 'de-DE',
        },
        {
            currentMarketId: 'enu-DE',
            portalMarketId: 'en_DE',
            phxMarketId: 'en-DE',
            odsyMarketId: 'ENU.DE',
            isoMarketId: 'en-DE',
        },
        {
            currentMarketId: 'enu-ES',
            portalMarketId: 'en_ES',
            phxMarketId: 'en-ES',
            odsyMarketId: 'ENU.ES',
            isoMarketId: 'en-ES',
        },
        {
            currentMarketId: 'spa-ES',
            portalMarketId: 'es_ES',
            phxMarketId: 'es-ES',
            odsyMarketId: 'ESM.ES',
            isoMarketId: 'es-ES',
        },
        {
            currentMarketId: 'fre-FR',
            portalMarketId: 'fr_FR',
            phxMarketId: 'fr-FR',
            odsyMarketId: 'FRC.FR',
            isoMarketId: 'fr-FR',
        },
        {
            currentMarketId: 'enu-GB',
            portalMarketId: 'en_UK',
            phxMarketId: 'en-UK',
            odsyMarketId: 'ENU.UK',
            isoMarketId: 'en-GB',
        },
        {
            currentMarketId: 'enu-HK',
            portalMarketId: 'en_HK',
            phxMarketId: 'en-HK',
            odsyMarketId: 'ENU.HK',
            isoMarketId: 'en-HK',
        },
        {
            currentMarketId: 'zht-HK',
            portalMarketId: 'zh_HK',
            phxMarketId: 'zh-HK',
            odsyMarketId: 'ZHT.HK',
            isoMarketId: 'zh-Hant-HK',
        },
        {
            currentMarketId: 'enu-ID',
            portalMarketId: 'en_ID',
            phxMarketId: 'en-ID',
            odsyMarketId: 'ENU.ID',
            isoMarketId: 'en-ID',
        },
        {
            currentMarketId: 'ind-ID',
            portalMarketId: 'in_ID',
            phxMarketId: 'in-ID',
            odsyMarketId: 'IND.ID',
            isoMarketId: 'id-ID',
        },
        {
            currentMarketId: 'enu-IN',
            portalMarketId: 'en_IN',
            phxMarketId: 'en-IN',
            odsyMarketId: 'ENU.IN',
            isoMarketId: 'en-IN',
        },
        {
            currentMarketId: 'enu-IT',
            portalMarketId: 'en_IT',
            phxMarketId: 'en-IT',
            odsyMarketId: 'ENU.IT',
            isoMarketId: 'en-IT',
        },
        {
            currentMarketId: 'ita-IT',
            portalMarketId: 'it_IT',
            phxMarketId: 'it-IT',
            odsyMarketId: 'ITA.IT',
            isoMarketId: 'it-IT',
        },
        {
            currentMarketId: 'jpn-JP',
            portalMarketId: 'jp_JP',
            phxMarketId: 'jp-JP',
            odsyMarketId: 'JPN.JP',
            isoMarketId: 'ja-JP',
        },
        {
            currentMarketId: 'kor-KR',
            portalMarketId: 'kr_KR',
            phxMarketId: 'kr-KR',
            odsyMarketId: 'KOR.KR',
            isoMarketId: 'ko-KR',
        },
        {
            currentMarketId: 'spa-MX',
            portalMarketId: 'es_MX',
            phxMarketId: 'es-MX',
            odsyMarketId: 'ESM.MX',
            isoMarketId: 'es-MX',
        },
        {
            currentMarketId: 'enu-MY',
            portalMarketId: 'en_MY',
            phxMarketId: 'en-MY',
            odsyMarketId: 'ENU.MY',
            isoMarketId: 'en-MY',
        },
        {
            currentMarketId: 'msa-MY',
            portalMarketId: 'ml_MY',
            phxMarketId: 'ml-MY',
            odsyMarketId: 'MAY.MY',
            isoMarketId: 'ms-MY',
        },
        {
            currentMarketId: 'zhs-MY',
            portalMarketId: 'zh_MY',
            phxMarketId: 'zh-MY',
            odsyMarketId: 'ZHS.MY',
            isoMarketId: 'zh-Hans-MY',
        },
        {
            currentMarketId: 'enu-NZ',
            portalMarketId: 'en_NZ',
            phxMarketId: 'en-NZ',
            odsyMarketId: 'ENU.NZ',
            isoMarketId: 'en-NZ',
        },
        {
            currentMarketId: 'zhs-NZ',
            portalMarketId: 'zh_NZ',
            phxMarketId: 'zh-NZ',
            odsyMarketId: 'ZHS.NZ',
            isoMarketId: 'zh-Hans-NZ',
        },
        {
            currentMarketId: 'enu-PH',
            portalMarketId: 'en_PH',
            phxMarketId: 'en-PH',
            odsyMarketId: 'ENU.PH',
            isoMarketId: 'en-PH',
        },
        {
            currentMarketId: 'enu-RO',
            portalMarketId: 'en_RO',
            phxMarketId: 'en-RO',
            odsyMarketId: 'ENU.RO',
            isoMarketId: 'en-RO',
        },
        {
            currentMarketId: 'ron-RO',
            portalMarketId: 'ro_RO',
            phxMarketId: 'ro-RO',
            odsyMarketId: 'RON.RO',
            isoMarketId: 'ro-RO',
        },
        {
            currentMarketId: 'enu-SG',
            portalMarketId: 'en_SG',
            phxMarketId: 'en-SG',
            odsyMarketId: 'ENU.SG',
            isoMarketId: 'en-SG',
        },
        {
            currentMarketId: 'zhs-SG',
            portalMarketId: 'zh_SG',
            phxMarketId: 'zh-SG',
            odsyMarketId: 'ZHS.SG',
            isoMarketId: 'zh-Hans-SG',
        },
        {
            currentMarketId: 'tha-TH',
            portalMarketId: 'th_TH',
            phxMarketId: 'th-TH',
            odsyMarketId: 'THA.TH',
            isoMarketId: 'th-TH',
        },
        {
            currentMarketId: 'zht-TW',
            portalMarketId: 'zh_TW',
            phxMarketId: 'zh-TW',
            odsyMarketId: 'ZHT.TW',
            isoMarketId: 'zh-Hant-TW',
        },
        {
            currentMarketId: 'enu-US',
            portalMarketId: 'en_US',
            phxMarketId: 'en-US',
            odsyMarketId: 'ENU.US',
            isoMarketId: 'en-US',
        },
        {
            currentMarketId: 'spa-US',
            portalMarketId: 'es_US',
            phxMarketId: 'es-US',
            odsyMarketId: 'ESM.US',
            isoMarketId: 'es-US',
        },
        {
            currentMarketId: 'zht-US',
            portalMarketId: 'zh_US',
            phxMarketId: 'zh-US',
            odsyMarketId: 'ZHT.US',
            isoMarketId: 'zh-Hant-US',
        },
        {
            currentMarketId: 'zhs-US',
            portalMarketId: 'zh_US',
            phxMarketId: 'zh-US',
            odsyMarketId: 'ZHS.US',
            isoMarketId: 'zh-Hans-US',
        },
    ],
    pcMarkets: [
        {
            currentMarketId: 'enu-AU_PC',
            portalMarketId: 'en_AU_PC',
            phxMarketId: 'en-AU',
            odsyMarketId: 'ENU.AU',
            isoMarketId: 'en-AU',
        },
        {
            currentMarketId: 'fre-CA_PC',
            portalMarketId: 'fr_CA_PC',
            phxMarketId: 'fr-CA',
            odsyMarketId: 'FRC.CA',
            isoMarketId: 'fr-CA',
        },
        {
            currentMarketId: 'enu-US_PC',
            portalMarketId: 'en_US_PC',
            phxMarketId: 'en-US',
            odsyMarketId: 'ENU.US',
            isoMarketId: 'en-US',
        },
        {
            currentMarketId: 'spa-US_PC',
            portalMarketId: 'es_US_PC',
            phxMarketId: 'es-US',
            odsyMarketId: 'ESM.US',
            isoMarketId: 'es-US',
        },
        {
            currentMarketId: 'zht-US_PC',
            portalMarketId: 'zh_US_PC',
            phxMarketId: 'zh-US',
            odsyMarketId: 'ZHT.US',
            isoMarketId: 'zh-Hant-US',
        },
    ],
};
const BAOYING_MARKET_DEFS = {
    markets: [
        {
            currentMarketId: 'zhs-CN',
            portalMarketId: 'zh_CN',
            phxMarketId: 'zh-CN',
            odsyMarketId: 'ZHS.CN',
            isoMarketId: 'zh-Hans-CN',
        },
    ],
    pcMarkets: [
        {
            currentMarketId: 'zhs-CN_PC',
            portalMarketId: 'zh_CN_PC',
            phxMarketId: 'zh-CN',
            odsyMarketId: 'ZHS.CN',
            isoMarketId: 'zh-Hans-CN',
        },
    ],
};
const PRELOAD = {
    usana: USANA_MARKET_DEFS,
    baoying: BAOYING_MARKET_DEFS,
};
const NO_PRELOAD = {
    usana: {
        markets: [],
        pcMarkets: [],
    },
    baoying: {
        markets: [],
        pcMarkets: [],
    },
};

var I18nHonorCookie;
(function (I18nHonorCookie) {
    I18nHonorCookie[I18nHonorCookie["ALWAYS"] = 0] = "ALWAYS";
    I18nHonorCookie[I18nHonorCookie["IF_URL_CONTAINS"] = 1] = "IF_URL_CONTAINS";
    I18nHonorCookie[I18nHonorCookie["NEVER"] = 2] = "NEVER";
    I18nHonorCookie[I18nHonorCookie["KEEP"] = 3] = "KEEP";
})(I18nHonorCookie || (I18nHonorCookie = {}));

class UsanaInternalMarketService {
    constructor(httpClient, cookieService, platformWrappedLocationService, platformWrappedWindowService, urlService, graphUri, preload) {
        this.httpClient = httpClient;
        this.cookieService = cookieService;
        this.platformWrappedLocationService = platformWrappedLocationService;
        this.platformWrappedWindowService = platformWrappedWindowService;
        this.urlService = urlService;
        this.graphUri = graphUri;
        this.preload = preload;
        this.i18nInfoSubject = new ReplaySubject(1);
        this.i18nInfo = {
            current: {},
        };
    }
    isBaoying() {
        return this.currentConfig.org === I18nOrganization.BAOYING;
    }
    isValid(marketId, active) {
        return new Promise((resolve, reject) => {
            this.i18nInfoSubject.subscribe(() => {
                resolve(this.findMarketById(marketId, false) != null);
            });
        });
    }
    correctedMarketId(marketId, active, useBackend) {
        return new Promise((resolve, reject) => {
            this.i18nInfoSubject.subscribe(() => {
                const found = this.findMarketById(marketId, true);
                if (found != null) {
                    resolve(found.currentMarketId);
                }
                else if (useBackend) {
                    this.getMarketFromBackend(marketId).then((backendMarketId) => {
                        resolve(backendMarketId);
                    });
                }
                else {
                    // couldn't find it and we don't want to check the backend.
                    resolve(null);
                }
            });
        });
    }
    /**
     * returns the current raw object, realize this isn't safe you should use the subject unless you know what you are doing
     */
    unsafeGetI18nInfo() {
        return Object.freeze(Object.assign({}, this.i18nInfo));
    }
    geti18NInfoSubject() {
        return this.i18nInfoSubject;
    }
    setMarketId(marketId) {
        const found = this.findMarketById(marketId, false);
        if (!!found && found.currentMarketId !== this.i18nInfo.current.currentMarketId) {
            this.i18nInfo.current = found;
            this.setLocaleCookie();
            this.i18nInfoSubject.next(this.i18nInfo);
        }
    }
    loadI18nInfo(config) {
        if ((!this.currentConfig || this.currentConfig.org === I18nOrganization.KEEP) && !this.i18nInfo.markets) {
            // our first REAL load where we specify an org
            // and we don't have any markets
            switch (config.org) {
                case I18nOrganization.USANA:
                    this.i18nInfo.markets = this.preload.usana.markets;
                    this.i18nInfo.pcMarkets = this.preload.usana.pcMarkets;
                    break;
                case I18nOrganization.BAOYING:
                    this.i18nInfo.markets = this.preload.baoying.markets;
                    this.i18nInfo.pcMarkets = this.preload.baoying.pcMarkets;
                    break;
                case I18nOrganization.BOTH:
                    this.i18nInfo.markets = this.preload.usana.markets.concat(this.preload.baoying.markets);
                    this.i18nInfo.pcMarkets = this.preload.usana.pcMarkets.concat(this.preload.baoying.pcMarkets);
                    break;
                default:
                    this.i18nInfo.markets = [];
                    this.i18nInfo.pcMarkets = [];
            }
            if (this.i18nInfo.markets.length + this.i18nInfo.pcMarkets.length > 0 && config.honorCookie !== I18nHonorCookie.NEVER) {
                const marketOptions = this.findMarketOptionsByMultiId(this.getLocaleCookieAndNavLanguages(), true);
                const url = this.platformWrappedLocationService.href();
                let weightedOptions = marketOptions.possibleMarkets
                    .concat(marketOptions.possiblePcMarkets)
                    .sort((a, b) => {
                    // if an item further down the array IS in the url lets move it up since it is more relevant
                    if (url.includes(a.currentMarketId)) {
                        return -1;
                    }
                    // otherwise keep in place
                    return 0;
                });
                if (config.honorCookie === I18nHonorCookie.IF_URL_CONTAINS) {
                    weightedOptions = weightedOptions.filter((m) => url.includes(m.currentMarketId));
                }
                if (weightedOptions.length > 0) {
                    this.i18nInfo.current = weightedOptions[0];
                }
                this.i18nInfoSubject.next(this.i18nInfo);
            }
        }
        const minConfig = config.diff(this.currentConfig);
        if (minConfig.requestAnything(this.currentConfig ? this.currentConfig.activeStatus : null)) {
            this.currentConfig = config.combined(this.currentConfig); // combine em
            this.loadI18nInfoFromBackend(minConfig);
        }
        else if (!this.currentConfig) {
            // we requested nothing and have no config
            throw new Error('Invalid Market config provided');
            // if we have a currentConfig lets just return what we previously had since its the same
            // else the replay subject will take care of us
        }
    }
    /**
     * forces this configuration to be loaded from the backend without any smarts
     * be VERY careful when using this
     * @param config
     */
    forceConfigLoad(config) {
        this.loadI18nInfoFromBackend(config);
    }
    getMarketFromBackend(marketId) {
        return this.httpClient
            .post(this.graphUri, {
            query: getMarket.query,
            variables: {
                marketId: marketId,
            },
        })
            .toPromise()
            .then((result) => {
            if (result.data && result.data[getMarket.operationName]) {
                return result.data[getMarket.operationName].currentMarketId;
            }
        }, () => null);
    }
    loadI18nInfoFromBackend(config) {
        const query = graphQueries.buildFromConfig(config);
        const props = [];
        if (config.org !== I18nOrganization.BOTH) {
            props.push(this.getOrgProp(config));
        }
        this.httpClient
            .post(this.graphUri, {
            query: query.query,
            variables: {
                marketQuery: {
                    active: config.activeStatusToBoolean(),
                    havingProperties: props,
                },
            },
        }, {
            headers: {
                PageUrl: this.platformWrappedLocationService.pathname() + this.platformWrappedLocationService.hash(),
            },
            withCredentials: true,
        })
            .toPromise()
            .then((result) => {
            const i18nInfo = result.data[query.operationName];
            if (i18nInfo) {
                this.parseI18nInfo(config, i18nInfo);
            }
            else {
                // no response so we had an error
                UsanaLoggingService.logMsg('ERROR, no i18nInfo');
            }
        }, (err) => {
            // this is not good
            UsanaLoggingService.logMsg(err);
        });
    }
    parseI18nInfo(config, i18nInfo) {
        // config informs us on what to move over
        if (config.getLanguages) {
            this.i18nInfo.languages = i18nInfo.languages;
        }
        if (config.getCountries) {
            this.i18nInfo.countries = i18nInfo.countries;
        }
        if (config.getCurrent) {
            this.i18nInfo.current = i18nInfo.current;
            this.setLocaleCookie();
        }
        if (config.getTranslationMarketId) {
            this.i18nInfo.translationMarketId = i18nInfo.translationMarketId;
        }
        if (config.getMarkets) {
            this.i18nInfo.markets = i18nInfo.markets;
        }
        if (config.getPcMarkets) {
            this.i18nInfo.pcMarkets = i18nInfo.pcMarkets;
        }
        if (config.getMarketRollup) {
            try {
                this.i18nInfo.marketRollup = JSON.parse(i18nInfo.marketRollup);
            }
            catch (e) {
                UsanaLoggingService.logMsg(e);
            }
        }
        if (config.getPcMarketRollup) {
            try {
                this.i18nInfo.pcMarketRollup = JSON.parse(i18nInfo.pcMarketRollup);
            }
            catch (e) {
                UsanaLoggingService.logMsg(e);
            }
        }
        this.i18nInfoSubject.next(this.i18nInfo);
    }
    getLocaleCookie() {
        return this.cookieService.get(LOCALE_COOKIE);
    }
    getLocaleCookieAndNavLanguages() {
        const cookieVal = this.getLocaleCookie();
        const theWindow = this.platformWrappedWindowService.getWindow();
        if (theWindow.navigator && theWindow.navigator.languages) {
            return [cookieVal].concat(theWindow.navigator.languages);
        }
        return [cookieVal];
    }
    setLocaleCookie() {
        const cd = new Date();
        // one year in the future!
        cd.setTime(cd.getTime() + 365 * 24 * 60 * 60 * 1000);
        this.cookieService.set(LOCALE_COOKIE, this.i18nInfo.current.portalMarketId, cd, '/', this.urlService.getCookieDomain(), false);
    }
    /**
     * find a singular market that matches with a preference towards non pc markets
     * @param marketId - the market Id we want to find
     * @param includeOtherApps - whether we should check other app market formats (portal, odsy, etc).
     */
    findMarketById(marketId, includeOtherApps) {
        const options = this.findMarketOptionsById(marketId, includeOtherApps);
        if (options.possibleMarkets.length > 0) {
            return options.possibleMarkets[0];
        }
        if (options.possiblePcMarkets.length > 0) {
            return options.possiblePcMarkets[0];
        }
    }
    findMarketOptionsByMultiId(marketIds, includeOtherApps) {
        const options = {
            possibleMarkets: [],
            possiblePcMarkets: [],
        };
        marketIds.forEach((marketId) => {
            const newPossibilities = this.findMarketOptionsById(marketId, includeOtherApps);
            options.possibleMarkets = options.possibleMarkets.concat(newPossibilities.possibleMarkets);
            options.possiblePcMarkets = options.possiblePcMarkets.concat(newPossibilities.possiblePcMarkets);
        });
        UsanaLoggingService.logFn(() => `for the marketIds ${JSON.stringify(marketIds)} found ${JSON.stringify(options)}`);
        return options;
    }
    findMarketOptionsById(marketId, includeOtherApps) {
        const options = {
            possibleMarkets: [],
            possiblePcMarkets: [],
        };
        if (!marketId) {
            return options;
        }
        if (this.i18nInfo.markets) {
            const matches = this.i18nInfo.markets.filter((m) => m.currentMarketId === marketId ||
                (includeOtherApps &&
                    (m.portalMarketId === marketId || m.odsyMarketId === marketId || m.phxMarketId === marketId || m.isoMarketId === marketId)));
            options.possibleMarkets = matches;
        }
        if (this.i18nInfo.pcMarkets) {
            const pcMatches = this.i18nInfo.pcMarkets.filter((m) => m.currentMarketId === marketId ||
                (includeOtherApps && (m.portalMarketId === marketId || m.odsyMarketId === marketId || m.phxMarketId === marketId)));
            options.possiblePcMarkets = pcMatches;
        }
        return options;
    }
    getOrgProp(config) {
        return {
            propName: config.org === I18nOrganization.BAOYING ? 'baoying' : 'usana',
            propValue: true,
            matchOnNull: false,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaInternalMarketService, deps: [{ token: i1.HttpClient }, { token: i2.CookieService }, { token: i3.PlatformWrappedLocationService }, { token: i3.PlatformWrappedWindowService }, { token: i4.UrlService }, { token: GRAPH_URL_MARKET }, { token: PRELOADED_INFO }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaInternalMarketService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaInternalMarketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.CookieService }, { type: i3.PlatformWrappedLocationService }, { type: i3.PlatformWrappedWindowService }, { type: i4.UrlService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [GRAPH_URL_MARKET]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PRELOADED_INFO]
                }] }]; } });

// NOTE: this isn't providedIn root on purpose
/**
 * lazy loaded modules will recreate this service and their injected config will update the root level internalMarketService definition
 */
class UsanaMarketService {
    constructor(usanaInternalMarketService, configs) {
        this.usanaInternalMarketService = usanaInternalMarketService;
        this.configs = configs;
        this.combinedConfig = new I18nInfoConfig(false, false, true, // ensure we always get current no matter what other apps say so that our guard works
        false, false, false, false, false, MarketActiveStatus.KEEP, // we don't care
        I18nOrganization.KEEP, // we don't care
        I18nHonorCookie.KEEP // we don't care
        );
        for (const conf of configs) {
            this.combinedConfig = conf.combined(this.combinedConfig);
        }
        // injection token for this and baoying
        this.usanaInternalMarketService.loadI18nInfo(this.combinedConfig);
    }
    isValid(marketId, active) {
        return this.usanaInternalMarketService.isValid(marketId, active);
    }
    correctedMarketId(marketId, active, useBackend) {
        return this.usanaInternalMarketService.correctedMarketId(marketId, active, useBackend);
    }
    /**
     * returns the current raw object, realize this isn't safe you should use the subject unless you know what you are doing
     */
    unsafeGetI18nInfo() {
        return this.usanaInternalMarketService.unsafeGetI18nInfo();
    }
    geti18NInfoSubject() {
        return this.usanaInternalMarketService.geti18NInfoSubject();
    }
    setMarketId(marketId) {
        this.usanaInternalMarketService.setMarketId(marketId);
    }
    forceReloadOfCurrentFromBackend() {
        this.usanaInternalMarketService.forceConfigLoad(new I18nInfoConfig(false, false, true, // the only thing we care about in this scenario
        false, false, false, false, false, this.combinedConfig.activeStatus, this.combinedConfig.org, this.combinedConfig.honorCookie));
    }
    isBaoying() {
        return this.usanaInternalMarketService.isBaoying();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketService, deps: [{ token: UsanaInternalMarketService }, { token: I18N_INFO_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: UsanaInternalMarketService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [I18N_INFO_CONFIG]
                }] }]; } });

class UsanaMarketsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsModule, imports: [CommonModule, UsanaSsrModule, UrlModule], exports: [UsanaSsrModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsModule, providers: [
            CookieService,
            UsanaMarketService,
            // UsanaInternalMarketService, //provided: inRoot
        ], imports: [CommonModule, UsanaSsrModule, UrlModule, UsanaSsrModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, UsanaSsrModule, UrlModule],
                    providers: [
                        CookieService,
                        UsanaMarketService,
                        // UsanaInternalMarketService, //provided: inRoot
                    ],
                    exports: [UsanaSsrModule],
                }]
        }] });

class UsanaMarketsPreloadModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsPreloadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsPreloadModule, imports: [UsanaMarketsModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsPreloadModule, providers: [
            {
                provide: PRELOADED_INFO,
                useValue: PRELOAD,
            },
        ], imports: [UsanaMarketsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsPreloadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [UsanaMarketsModule],
                    providers: [
                        {
                            provide: PRELOADED_INFO,
                            useValue: PRELOAD,
                        },
                    ],
                    exports: [
                    // UsanaMarketsModule
                    ],
                }]
        }] });

class UsanaMarketsNoPreloadModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsNoPreloadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsNoPreloadModule, imports: [UsanaMarketsModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsNoPreloadModule, providers: [
            {
                provide: PRELOADED_INFO,
                useValue: NO_PRELOAD,
            },
        ], imports: [UsanaMarketsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsNoPreloadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [UsanaMarketsModule],
                    providers: [
                        {
                            provide: PRELOADED_INFO,
                            useValue: NO_PRELOAD,
                        },
                    ],
                    exports: [
                    // UsanaMarketsModule
                    ],
                }]
        }] });

/*
 * Public API Surface of usana-markets
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BAOYING_MARKET_DEFS, GRAPH_URL_MARKET, I18N_INFO_CONFIG, I18nHonorCookie, I18nInfoConfig, I18nOrganization, MarketActiveStatus, USANA_MARKET_DEFS, UsanaMarketService, UsanaMarketsNoPreloadModule, UsanaMarketsPreloadModule };
